import { forwardRef, type Ref } from 'react';
import type { ChangeEvent } from 'react';

import { Switch as MuiSwitch, SwitchProps as MuiSwitchProps } from '@mui/material';
import { Switch as DsSwitch } from '@natter-co/design-system';
import { VISUAL_REFRESH } from 'common/utils';

import { toDsProps } from './toDsProps';

export type SwitchProps = Pick<
  MuiSwitchProps,
  | 'aria-label'
  // Added for the visual refresh. The DS switch names itself from `label` / `aria-label` /
  // `aria-labelledby`, and `aria-labelledby` is the one the design's own settings rows use — the row
  // heading IS the control's name, so pointing at it beats duplicating it into an `aria-label` and
  // having a screen reader announce the setting twice. `SwitchBox` needs it for a second reason: it
  // associates its external label with `htmlFor`, which binds only to labelable elements, and Base
  // UI puts the id on a non-labelable root.
  | 'aria-labelledby'
  | 'checked'
  | 'defaultChecked'
  | 'disabled'
  | 'id'
  | 'name'
  | 'size'
  | 'onChange'
  | 'value'
  | 'sx'
>;

/**
 * The standalone switch: no label of its own, named by `aria-label` or `aria-labelledby`.
 *
 * The visual-refresh gate lives here, and only for the standalone case. A switch inside a
 * `FormControlLabel` must not branch — the DS switch is itself a `<label>`, so the two would nest —
 * which is why `ReactHookForm/Switch` and `ChatModerationSwitch` render `MuiSwitch` directly and
 * own their own DS branch. `toDsProps` returns `null` for anything DS 2.0 cannot express, which
 * falls through to the MUI implementation.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(({ onChange, ...props }, ref) => {
  const dsProps = VISUAL_REFRESH ? toDsProps(props) : null;

  if (dsProps) {
    const { checked, defaultChecked, disabled, id, name, value } = props;

    return (
      <DsSwitch
        {...dsProps}
        // The DS component types its ref as `HTMLElement`; Base UI puts it on the `<button>` root, which is
        // where the MUI `ref` landed too. `Ref` is invariant in its element, hence the double cast.
        ref={ref as unknown as Ref<HTMLElement>}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        id={id}
        name={name}
        value={value as string | undefined} // narrowed by `toDsProps`
        /**
         * The legacy contract is MUI's `(event, checked)`. Base UI reports `(checked, details)` with the
         * native event inside, so the adapter hands that event back in the legacy position; every caller
         * reads `checked` from the second argument, and none reads the event beyond `target.checked`.
         */
        onCheckedChange={(nextChecked, details) =>
          onChange?.(details.event as unknown as ChangeEvent<HTMLInputElement>, nextChecked)
        }
      />
    );
  }

  return (
    <MuiSwitch
      ref={ref}
      onChange={onChange}
      {...props}
      inputProps={{ 'aria-label': props['aria-label'], 'aria-labelledby': props['aria-labelledby'] }}
    />
  );
});
